import type { CategoryInput, ProductInput, ShopSettings, SubcategoryInput } from "./types";

export interface ValidationResult<T> {
  ok: boolean;
  value?: T;
  errors: Record<string, string>;
}

const clean = (value: unknown, max: number) =>
  typeof value === "string" ? value.trim().slice(0, max) : "";

const paired = (
  left: string,
  right: string,
  leftKey: string,
  rightKey: string,
  errors: Record<string, string>,
) => {
  if (!!left !== !!right) {
    errors[left ? rightKey : leftKey] = "دەبێت هەردوو وەرگێڕانەکە پڕ بکرێنەوە یان هەردووکیان بەتاڵ بن.";
  }
};

export function validateProduct(input: unknown): ValidationResult<ProductInput> {
  const source = input && typeof input === "object" ? (input as Record<string, unknown>) : {};
  const value: ProductInput = {
    title_ku: clean(source.title_ku, 160),
    title_ar: clean(source.title_ar, 160),
    description_ku: clean(source.description_ku, 1500),
    description_ar: clean(source.description_ar, 1500),
    color_ku: clean(source.color_ku, 80),
    color_ar: clean(source.color_ar, 80),
    price_iqd: Number(source.price_iqd),
    category_id: clean(source.category_id, 100),
    subcategory_id: clean(source.subcategory_id, 100),
    related_ids: Array.isArray(source.related_ids)
      ? [...new Set(source.related_ids.filter((item): item is string => typeof item === "string").map((item) => item.trim()).filter(Boolean))].slice(0, 50)
      : [],
  };
  const errors: Record<string, string> = {};
  if (Array.isArray(source.related_ids) && source.related_ids.filter((item) => typeof item === "string" && item.trim()).length !== value.related_ids?.length) errors.related_ids = "بەرهەمی دووبارە یان ناسراو نەکراو ڕێگەپێدراو نییە.";
  if (!value.title_ku) errors.title_ku = "ناوی کوردی پێویستە.";
  if (!value.title_ar) errors.title_ar = "ناوی عەرەبی پێویستە.";
  if (!value.category_id) errors.category_id = "جۆری بەرهەم پێویستە.";
  paired(value.description_ku || "", value.description_ar || "", "description_ku", "description_ar", errors);
  paired(value.color_ku || "", value.color_ar || "", "color_ku", "color_ar", errors);
  if (!Number.isSafeInteger(value.price_iqd) || value.price_iqd <= 0 || value.price_iqd > 999_999_999) {
    errors.price_iqd = "نرخێکی دروست و گەورەتر لە سفر بنووسە.";
  }
  return { ok: Object.keys(errors).length === 0, value, errors };
}

export function normalizeLocalizedName(input: unknown) {
  return clean(input, 120)
    .normalize("NFKC")
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, "")
    .replace(/ـ/g, "")
    .replace(/[أإآٱ]/g, "ا")
    .replace(/ك/g, "ک")
    .replace(/[يى]/g, "ی")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase();
}

export function validateCategory(input: unknown): ValidationResult<CategoryInput> {
  const source = input && typeof input === "object" ? input as Record<string, unknown> : {};
  const value = { name_ku: clean(source.name_ku, 80), name_ar: clean(source.name_ar, 80) };
  const errors: Record<string, string> = {};
  if (!value.name_ku) errors.name_ku = "ناوی کوردی پێویستە.";
  if (!value.name_ar) errors.name_ar = "ناوی عەرەبی پێویستە.";
  return { ok: Object.keys(errors).length === 0, value, errors };
}

export function validateSubcategory(input: unknown): ValidationResult<SubcategoryInput> {
  const source = input && typeof input === "object" ? input as Record<string, unknown> : {};
  const value = {
    parent_category_id: clean(source.parent_category_id, 100),
    name_ku: clean(source.name_ku, 80),
    name_ar: clean(source.name_ar, 80),
  };
  const errors: Record<string, string> = {};
  if (!value.parent_category_id) errors.parent_category_id = "پۆلی سەرەکی پێویستە.";
  if (!value.name_ku) errors.name_ku = "ناوی کوردی پێویستە.";
  if (!value.name_ar) errors.name_ar = "ناوی عەرەبی پێویستە.";
  return { ok: Object.keys(errors).length === 0, value, errors };
}

export function normalizePhone(input: unknown) {
  const raw = clean(input, 32).replace(/[\s()-]/g, "");
  let digits = raw.replace(/^\+/, "");
  if (/^07\d{9}$/.test(digits)) digits = `964${digits.slice(1)}`;
  if (!/^9647\d{9}$/.test(digits)) return null;
  return { display: `0${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`, international: `+${digits}`, wa: digits };
}

function safeSocialUrl(input: unknown, hosts: string[]) {
  const value = clean(input, 400);
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    if (parsed.protocol !== "https:" || !hosts.some((allowed) => host === allowed || host.endsWith(`.${allowed}`))) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function validateSettings(input: unknown): ValidationResult<ShopSettings> {
  const source = input && typeof input === "object" ? (input as Record<string, unknown>) : {};
  const phone = normalizePhone(source.phone);
  const tiktok = safeSocialUrl(source.tiktok_url, ["tiktok.com"]);
  const instagram = safeSocialUrl(source.instagram_url, ["instagram.com"]);
  const errors: Record<string, string> = {};
  if (!phone) errors.phone = "ژمارەی مۆبایلێکی عێراقی دروست بنووسە.";
  if (!tiktok) errors.tiktok_url = "بەستەری دروستی TikTok بە https بنووسە.";
  if (!instagram) errors.instagram_url = "بەستەری دروستی Instagram بە https بنووسە.";
  return {
    ok: Object.keys(errors).length === 0,
    value: phone && tiktok && instagram ? { phone: phone.display, tiktok_url: tiktok, instagram_url: instagram } : undefined,
    errors,
  };
}
